import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { makeStyles } from 'tss-react/mui';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { useSelector } from 'react-redux';
import { selectOrderWorkHistory } from '@AMIEWEB/NewOrder/Store/Order.selectors';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .Mui-disabled .MuiFilledInput-input': {
      visibility: 'hidden',
    },
    '& .MuiFilledInput-input': {
      paddingTop: '20px',
    },
    '& .MuiFormLabel-filled': {
      font: '12px Roboto',
    },
  },
  checkbox: {
    '& label': {
      '& .MuiButtonBase-root.MuiCheckbox-root.Mui-checked': {
        color: `${theme.palette.system.main} !important`,
      },
      '&:hover .MuiButtonBase-root.MuiCheckbox-root': {
        color: `${theme.palette.system.main} !important`,
      },
    },
  },
}));

export const WorkHistoryContainer = () => {
  const { t } = useTranslation();
  const { control, watch, setValue, formState: { errors }, clearErrors, getValues } = useFormContext();
  const { classes } = useStyles();
  const orderWorkHistory = useSelector(selectOrderWorkHistory);
  const [workHistoryGapInYearsValueHolder, setworkHistoryGapInYearsValueHolder] = useState(null);

  const checkWorkHistory = (value: string) => {
    const workHistoryGapInDays = watch('orderWorkHistory.workHistoryGapInDays');
    const entireWorkHistoryChecked = watch('orderWorkHistory.entireWorkHistory');
    if (
      parseInt(value) === 0 ||
      (value === null && workHistoryGapInDays !== null && workHistoryGapInDays > -1 && !entireWorkHistoryChecked)
    ) {
      return t('facility.units.unitDetails.additionalInformation.workHistoryGap.validation');
    } else {
      return null;
    }
  };

  const formatYearMonthValue = value => {
    const number = parseInt(value, 10);
    if (isNaN(number) || number < 0 || number === null) {
      return null;
    } else {
      return number;
    }
  };

  return (
    <Grid container columnSpacing={2} marginTop={0}>
      <Grid item xs={6} columnSpacing={2} style={{ display: 'flex', flexDirection: 'column' }}>
        <Grid item xs={12} style={{ marginBottom: '12px' }}>
          {/* No of Gaps Greater than days */}
          <Controller
            name={'orderWorkHistory.workHistoryGapInDays'}
            control={control}
            render={({ field }) => (
              <SingleLineInput
                id={'orderWorkHistory.workHistoryGapInDays'}
                inputDefaultValue={orderWorkHistory?.workHistoryGapInDays ?? null}
                inputLabel={t('facility.units.unitDetails.additionalInformation.workHistoryGap.noGapsGreaterThan')}
                inputName={'orderWorkHistory.workHistoryGapInDays'}
                inputValue={field.value}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const intValue = formatYearMonthValue(event.target.value);
                  if (intValue === null) {
                    clearErrors('orderWorkHistory.workHistoryGapInYears');
                  }
                  field.onChange(intValue);
                }}
                showEndAdornment={true}
                endAdornmentValue={'days'}
                inputType={'number'}
                shrinkLabel={true}
                fieldStyle={classes.fieldStyle}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          {/* Within Last Years */}
          <Controller
            name={'orderWorkHistory.workHistoryGapInYears'}
            control={control}
            rules={{ validate: checkWorkHistory }}
            render={({ field }) => (
              <SingleLineInput
                id={'orderWorkHistory.workHistoryGapInYears'}
                inputDefaultValue={orderWorkHistory?.workHistoryGapInYears ?? null}
                inputLabel={t('facility.units.unitDetails.additionalInformation.workHistoryGap.withinTheLast')}
                inputName={'orderWorkHistory.workHistoryGapInYears'}
                inputValue={field.value}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(formatYearMonthValue(event.target.value));
                }}
                inputError={errors?.orderWorkHistory?.workHistoryGapInYears?.message}
                showEndAdornment={true}
                helperText={errors?.orderWorkHistory?.workHistoryGapInYears?.message}
                endAdornmentValue={'years'}
                inputType={'number'}
                fieldStyle={classes.fieldStyle}
                isdisabled={watch('orderWorkHistory.entireWorkHistory')}
                shrinkLabel={!watch('orderWorkHistory.entireWorkHistory')}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={6} columnSpacing={2} style={{ display: 'flex', flexDirection: 'column' }}>
        <Grid item className={classes.checkbox} style={{ marginTop: '80px', paddingLeft: '11px' }}>
          {/* Entire work History */}
          <Controller
            name={'orderWorkHistory.entireWorkHistory'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                defaultValue={orderWorkHistory?.entireWorkHistory ?? false}
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                  if (!event.target.checked) {
                    setValue('orderWorkHistory.workHistoryGapInYears', workHistoryGapInYearsValueHolder, { shouldValidate: true });
                  } else {
                    clearErrors('orderWorkHistory.workHistoryGapInYears');
                    setworkHistoryGapInYearsValueHolder(getValues('orderWorkHistory.workHistoryGapInYears'));
                    setValue('orderWorkHistory.workHistoryGapInYears', null, { shouldValidate: true });
                  }
                }}
                checkboxLabel={t('facility.units.unitDetails.additionalInformation.workHistoryGap.entireWorkHistory')}
                checkboxName={'orderWorkHistory.entireWorkHistory'}
                id={'orderWorkHistory.entireWorkHistory'}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
